<template>
    <SbsSelect
        v-bind="selectData"
        v-model="selectModel"
        v-model:opened="isSelectOpened"
        ref="select"
        class="sbs-combobox trs"
        @focusedIndex:change="focusIndexChangeHandler">
        <!-- <template> -->
            <!-- @slot Слот по-умолчанию для активатора -->
            <slot
                v-bind="{
                    disabled,
                    opened: isSelectOpened,
                    selected: selectedItems,
                }">
                <SbsInput
                    v-bind="searchData"
                    v-model="searchModel"
                    class="sbs-combobox__toggler"
                    @focus="isSelectOpened = true"
                    @keydown.capture.delete="deleteLastItem"
                    @keydown.esc="$event.target.blur()">
                    <template #prepend>
                        <div
                            v-if="selectedItems.length"
                            class="sbs-combobox__chips">
                            <SbsChip
                                v-bind="chipData"
                                class="sbs-combobox__chip"
                                @click="deleteItem(selectedItems[0])">
                                <template #append>
                                    <SbsIcon
                                        v-bind="chipIconData"
                                        class="sbs-combobox__chip-icon" />
                                </template>
                            </SbsChip>
                            <SbsChip
                                v-if="selectedItems.length > 1"
                                class="sbs-combobox__chip">
                                +{{ selectedItems.length - 1 }}
                            </SbsChip>
                        </div>
                    </template>

                    <template #append>
                        <SbsButtonIcon
                            v-bind="togglerIconData"
                            class="sbs-combobox__toggler-icon trs"
                            @click.stop="isSelectOpened = !isSelectOpened" />
                    </template>
                </SbsInput>
            </slot>
        <!-- </template> -->

        <template #prepend>
            <!-- @slot Слот для вставки контента перед списком элементов -->
            <slot name="prepend"></slot>
        </template>

        <template
            #list="{
                items: selectItems,
                isItemSelected,
            }">
            <!-- @slot Слот для списка элементов -->
            <slot
                v-bind="{
                    items: selectItems,
                    isItemSelected,
                }"
                name="list">
                <SbsSelectListItem
                    v-for="(item, index) in selectItems"
                    ref="comboboxItem"
                    :key="index"
                    labelClass="px-4 rounded-0"
                    :label="item.label"
                    :modelValue="item.value" />
            </slot>
        </template>

        <template #append>
            <!-- @slot Слот для вставки контента после списка элементов -->
            <slot name="append"></slot>
        </template>

        <template #empty>
            <!-- @slot Слот при отсутствии элементов -->
            <slot name="empty">
                <div class="sbs-combobox__empty-search">
                    <span class="sbs-combobox__empty-search-text">Ничего не найдено</span>
                    <span>Попробуйте изменить поисковой запрос</span>
                </div>
            </slot>
        </template>
    </SbsSelect>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import { AllColors } from '../../utils/colors.js';
import dimensionsMixin from '../../mixins/dimensions.js';

const DEFAULT_POPUP_OPTIONS = {
    maxHeight: 250,
    offset: 10,
};

export default {
    name: 'SbsCombobox',
    mixins: [themesMixin, dimensionsMixin],
    emits: ['update:modelValue', 'update:opened', 'update:searchQuery', 'afterEnter'],
    data() {
        return {
            inputWidth: null,
            resizeObserver: null,
        };
    },
    computed: {
        selectModel: {
            get() {
                return this.modelValue;
            },
            set(value) {
                /**
                 * Эмит события `update:modelValue`, срабатывает при выборе элемента в списке
                 */
                this.emitValue(value);
            },
        },
        isSelectOpened: {
            get() {
                return this.opened;
            },
            set(value) {
                /**
                 * Эмит события `update:opened`, отвечает за открытие селекта
                 */
                this.$emit('update:opened', value);
            },
        },
        selectClasses() {
            return [
                {
                    'disabled': this.disabled,
                    'opened': this.isSelectOpened,
                    'selected': this.selectedItems.length,
                    'full-width': this.fullWidth,
                },
            ];
        },
        tooglerIconClasses() {
            return {
                'fa-rotate-180': this.isSelectOpened,
            };
        },
        inputStyle() {
            return {
                width: this.inputWidth,
            };
        },
        selectPopupListeners() {
            return {
                ...this.popupListeners,
                afterEnter: this.afterEnterHandler,
            };
        },
        selectData() {
            return {
                class: this.selectClasses,
                disabled: this.disabled,
                items: this.filteredItems,
                popupListeners: this.selectPopupListeners,
                popupProps: this.popupData,
                style: this.dimensionStyles,
                selectListGroupClass: 'sbs-combobox__list-group',
            };
        },
        chipData() {
            return {
                bordered: true,
                borderTheme: AllColors.TRANSPARENT,
                hoverBorder: AllColors.GRAY_DEEP,
                text: this.selectedItems[0].label,
            };
        },
        chipIconData() {
            return {
                iconName: 'times',
                hoverColor: AllColors.GRAY_DEEP,
            };
        },
        popupData() {
            return {
                ...DEFAULT_POPUP_OPTIONS,
                ...this.popupProps,
                popupClass: 'sbs-combobox__popup',
                popupContentClass: 'sbs-combobox__popup-content',
                width: this.inputWidth,
                minWidth: this.$el?.offsetWidth,
                fullWidth: this.fullWidth,
                zIndex: 10,
            };
        },
        searchData() {
            return {
                bordered: false,
                disabled: this.disabled,
                fullWidth: true,
                inputFieldClass: 'cursor-pointer',
                inputWrapperClass: 'group cursor-pointer',
                label: this.label,
                style: this.inputStyle,
            };
        },
        togglerIconData() {
            return {
                class: this.tooglerIconClasses,
                disabled: this.disabled, // отключается возможность управлять при дизейбле через клавиатуру
                iconName: 'chevron-down',
                hoverColor: AllColors.WHITE,
                width: 28,
                height: 28,
            };
        },
        searchModel: {
            get() {
                return this.searchQuery;
            },
            set(value) {
                /**
                 * Эмит события `update:searchQuery`, отвечает за поиск среди элементов комбобокса
                 */
                this.$emit('update:searchQuery', value);
            },
        },
        selectedItems() {
            return this.selectModel.map(item => this.items
                .find(({ value }) => item === value));
        },
        filteredItems() {
            return this.items
                .filter(item => item.label.toLowerCase()
                    .includes(this.searchQuery.toLowerCase()));
        },
    },
    methods: {
        emitValue(value) {
            /**
             * Эмит события `change`, срабатывает при выборе элемента в списке
             */
            this.$emit('update:modelValue', value);
        },
        deleteItem({ value }) {
            const emittedValue = this.selectModel
                .filter(item => item !== value);

            /**
             * Эмит события `change`, срабатывает при удалении первого выбранного чипа
             */
            this.emitValue(emittedValue);
        },
        deleteLastItem() {
            if (
                !this.selectModel.length ||
                this.searchQuery.length > 0
            ) return;

            const emittedValue = [...this.selectModel];

            emittedValue.pop();

            /**
             * Эмит события `change`, срабатывает при удалении чипа после нажатия на клавиши Backspace / Delete
             */
            this.emitValue(emittedValue);
        },
        setSelectCssVarTheme(colorTheme, el = this.$el) {
            if (!colorTheme) return;

            el.style.setProperty(
                '--select-color-theme',
                `var(--${colorTheme})`,
            );
        },
        afterEnterHandler(el) {
            this.setSelectCssVarTheme(this.colorTheme, el);
            /**
             * Эмит события `afterEnter`, отвечает за анимацию отрисовки
             */
            this.$emit('afterEnter', el);
        },
        getInputWidth() {
            this.inputWidth = this.fullWidth ? '100%' : this.$el.offsetWidth;
        },
        focusIndexChangeHandler(value) {
            const focusedItem = this.$refs.comboboxItem?.[value]?.$refs?.checkbox?.$refs?.input;

            if (focusedItem) {
                focusedItem.focus();
            }
        },
        updateFocusedIndex() {
            this.$refs.select.updateFocusedIndex();
        },
    },
    props: {
        /**
         * Отключает взаимодествие с комбобоксом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет будет ли комбобокс и попап занимать всю доступную ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Пункты выпадающего списка
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Лейбл для поискового инпута
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Отвечает за отображение списка элементов комбобокса
         */
        opened: {
            type: Boolean,
            default: false,
        },
        /**
         * Эмит событий для попапа
         */
        popupListeners: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Данные для попапа
         */
        popupProps: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Поисковый запрос
         */
        searchQuery: {
            type: String,
            default: '',
        },
        /**
         * Список выбранных значений в комбобоксе
         * @model
         */
        modelValue: {
            type: Array,
            default: () => [],
        },
    },
    watch: {
        colorTheme(val) {
            this.setSelectCssVarTheme(val);
        },
        searchModel() {
            this.updateFocusedIndex();
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.getInputWidth);
    },
    mounted() {
        this.setSelectCssVarTheme(this.colorTheme);
        this.getInputWidth();

        if (this.$el) {
            this.resizeObserver.observe(this.$el);
        }
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

.<style lang="scss">
.sbs-combobox {
    --select-color-theme: #{$success};

    &.disabled {
        cursor: not-allowed;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &.full-width {
        width: 100%;
    }

    &__toggler {
        border: 1px solid $gray;
        border-radius: $rounded-10;
        transition: $trs;

        @include has-hover {
            &:hover {
                border-color: var(--select-color-theme);
            }
        }

        .sbs-combobox.opened & {
            border-color: var(--select-color-theme);
        }

        .sbs-input__field-wrapper {
            position: static;
        }

        .sbs-input__label {
            right: 15px;
            left: 15px;

            .sbs-combobox.selected & {
                top: -1px;

                @include typo-subsecondary-demi;
            }
        }

        .sbs-input__label-text {
            .sbs-combobox.selected & {
                background-color: $white;
            }

            .sbs-combobox.selected.disabled & {
                color: rgba($gray-deep, 0.4);
                background-image:
                    linear-gradient(
                        180deg,
                        $white 0%,
                        $white 45%,
                        $gray-ultra-light 50%,
                        $gray-ultra-light 100%
                    );
                opacity: 1;
            }
        }
    }

    &__chips {
        display: flex;
        max-width: 67.5%;
        margin-right: 10px;
    }

    &__chip {
        margin-right: 10px;
        overflow: hidden;

        @include typo-subsecondary-demi;

        &:not(:first-child) {
            flex-shrink: 0;
        }

        &:last-child {
            margin-right: 0;
        }

        .sbs-combobox.disabled & {
            opacity: 0.4;
        }

        .sbs-chip__text {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }

    &__chip-icon {
        margin-left: 5px;
    }

    &__toggler-icon {
        z-index: 1;
        margin-left: 10px;
        color: $black;
        background-color: $gray-ultra-light;
        border-radius: $rounded-circle;

        @include has-hover {
            &:hover {
                background-color: var(--select-color-theme);
            }
        }

        .sbs-combobox.disabled & {
            color: $gray-deep;
            background-color: $white;
            user-select: none;
        }
    }

    &__popup {
        --select-color-theme: #{$success};

        &.sbs-popup {
            padding-top: 20px;
            border-radius: $rounded-5 $rounded-5 $rounded-10 $rounded-10; // top-left, top-right, bottom-right, bottom-left
        }
    }

    &__popup-content {
        @include scrollbar(
            var(--scrollbar-width),
            $gray,
            var(--select-color-theme),
            var(--select-color-theme),
            $transparent,
        );
    }

    &__empty-search {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 75px 35px;
        color: $gray-deep;
        text-align: center;
    }

    &__empty-search-text {
        @include typo-subprimary-medium;
    }
}
</style>
